// ./src/utils/imageUtils.ts

export interface ImageResizeConfig {
  maxWidth: number;
  maxHeight: number;
  quality?: number;
}

export interface ImageResizeResult {
  file: File;
  wasResized: boolean;
  originalDimensions?: { width: number; height: number };
  newDimensions?: { width: number; height: number };
}

export interface ImageValidationResult {
  isValid: boolean;
  error?: string;
}

export const DEFAULT_IMAGE_RESIZE_CONFIG: ImageResizeConfig = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.9,
};

export const SUPPORTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/heic",
  "image/avif",
  "image/webp",
] as const;

export type SupportedImageType = (typeof SUPPORTED_IMAGE_TYPES)[number];

/**
 * Validates if the provided file is a supported image type
 */
export const validateImageFile = (file: File): ImageValidationResult => {
  if (!file) {
    return {
      isValid: false,
      error: "No file provided",
    };
  }

  if (!SUPPORTED_IMAGE_TYPES.includes(file.type as SupportedImageType)) {
    const supportedFormats = SUPPORTED_IMAGE_TYPES.map((type) =>
      type.split("/")[1].toUpperCase()
    ).join(", ");

    return {
      isValid: false,
      error: `Unsupported file type. Supported formats: ${supportedFormats}`,
    };
  }

  return { isValid: true };
};

/**
 * Gets formatted list of supported image file extensions for file input accept attribute
 */
export const getSupportedImageTypesString = (): string => {
  return SUPPORTED_IMAGE_TYPES.join(",");
};

/**
 * Gets human-readable list of supported image formats
 */
export const getSupportedFormatsDescription = (): string => {
  return SUPPORTED_IMAGE_TYPES.map((type) =>
    type.split("/")[1].toUpperCase()
  ).join(", ");
};

/**
 * Gets image dimensions from a File object
 */
export const getImageDimensions = (
  file: File
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Calculates new dimensions for image resize while maintaining aspect ratio
 */
export const calculateResizeDimensions = (
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number; shouldResize: boolean } => {
  // Check if resize is needed
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return {
      width: originalWidth,
      height: originalHeight,
      shouldResize: false,
    };
  }

  // Calculate scale factor to fit within bounds
  const widthRatio = maxWidth / originalWidth;
  const heightRatio = maxHeight / originalHeight;
  const scale = Math.min(widthRatio, heightRatio);

  return {
    width: Math.round(originalWidth * scale),
    height: Math.round(originalHeight * scale),
    shouldResize: true,
  };
};

/**
 * Resizes an image file if it exceeds the specified dimensions
 */
export const resizeImageIfNeeded = async (
  file: File,
  config: ImageResizeConfig = DEFAULT_IMAGE_RESIZE_CONFIG
): Promise<ImageResizeResult> => {
  // Validate file first
  const validation = validateImageFile(file);
  if (!validation.isValid) {
    throw new Error(validation.error || "Invalid image file");
  }

  try {
    // Get original dimensions
    const originalDimensions = await getImageDimensions(file);

    // Calculate new dimensions
    const {
      width: newWidth,
      height: newHeight,
      shouldResize,
    } = calculateResizeDimensions(
      originalDimensions.width,
      originalDimensions.height,
      config.maxWidth,
      config.maxHeight
    );

    if (!shouldResize) {
      return {
        file,
        wasResized: false,
        originalDimensions,
      };
    }

    // Perform resize
    const resizedFile = await performImageResize(
      file,
      newWidth,
      newHeight,
      config.quality || 0.9
    );

    return {
      file: resizedFile,
      wasResized: true,
      originalDimensions,
      newDimensions: { width: newWidth, height: newHeight },
    };
  } catch (error) {
    throw new Error(
      `Failed to process image: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};

/**
 * Performs the actual image resize using canvas
 */
const performImageResize = (
  file: File,
  newWidth: number,
  newHeight: number,
  quality: number = 0.9
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Failed to get canvas context"));
      return;
    }

    img.onload = () => {
      // Set canvas dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Draw and resize image
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          } else {
            reject(new Error("Failed to create resized file"));
          }
          URL.revokeObjectURL(img.src);
        },
        file.type,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image for resizing"));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Creates a resize note message for user feedback
 */
export const createResizeNote = (
  originalDimensions: { width: number; height: number },
  newDimensions: { width: number; height: number }
): string => {
  return `Image was resized from ${originalDimensions.width}x${originalDimensions.height} to ${newDimensions.width}x${newDimensions.height} to meet size requirements.`;
};

/**
 * Creates upload notes for file metadata
 */
export const createUploadNotes = (
  wasResized: boolean,
  originalDimensions?: { width: number; height: number },
  newDimensions?: { width: number; height: number }
): string | undefined => {
  if (!wasResized || !originalDimensions || !newDimensions) {
    return undefined;
  }

  return `Resized from original dimensions ${originalDimensions.width}x${originalDimensions.height} to ${newDimensions.width}x${newDimensions.height}`;
};
